import React from 'react';
import { Link } from 'react-router-dom';
import { Layout, Code2, Users, RotateCcw, ShieldCheck, CheckCircle2, ArrowRight, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../config/siteConfig';
import PageHeader from '../components/PageHeader';

const Services: React.FC = () => {
  const { t } = useLanguage();

  const services = [
    {
      title: t.services.architecture.title,
      icon: Layout,
      description: t.services.architecture.desc,
      points: t.services.architecture.points,
      tech: undefined as string | undefined,
    },
    {
      title: t.services.engineering.title,
      icon: Code2,
      description: t.services.engineering.desc,
      points: t.services.engineering.points,
      tech: t.services.engineering.tech,
    },
    {
      title: t.services.coaching.title,
      icon: Users,
      description: t.services.coaching.desc,
      points: t.services.coaching.points,
      tech: undefined as string | undefined,
    },
    {
      title: t.services.recovery.title,
      icon: RotateCcw,
      description: t.services.recovery.desc,
      points: t.services.recovery.points,
      tech: undefined as string | undefined,
    }
  ];

  return (
    <div className="bg-maat-stone pb-32">
      {/* Page Header */}
      <PageHeader
        title={t.services.title}
        titleAccent={t.services.titleAccent}
        subtitle={t.services.subtitle}
      />

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 md:p-8 rounded-4xl border border-maat-sand hover:border-maat-gold transition-all duration-700 group flex flex-col h-full shadow-sm hover:shadow-xl">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-2xl font-serif text-maat-blue group-hover:text-maat-gold transition-colors">{service.title}</h2>
                  <div className="p-3 bg-maat-stone rounded-2xl group-hover:bg-maat-gold/10 transition-colors">
                    <service.icon className="text-maat-gold opacity-60 group-hover:opacity-100 transition-opacity" size={28} strokeWidth={1.5} />
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-base text-maat-blue/70 leading-relaxed">
                    {service.description}
                  </p>
                </div>

                <div className="flex-grow">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-maat-gold mb-4">{t.services.focusTitle}</h4>
                  <div className="space-y-3.5 mb-8">
                    {service.points.map((point: string, pIndex: number) => (
                      <div key={pIndex} className="flex items-start space-x-3">
                        <div className="mt-1.5 w-1.5 h-1.5 bg-maat-gold rounded-full flex-shrink-0" />
                        <span className="text-sm text-maat-blue/80 font-medium leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                  {service.tech && (
                    <div className="mb-2">
                      <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-maat-gold mb-3">{t.services.techTitle}</h4>
                      <div className="flex flex-wrap gap-2">
                        {service.tech.split(',').map((techItem: string, tIdx: number) => (
                          <span key={tIdx} className="text-xs font-medium text-maat-blue/80 bg-maat-stone border border-maat-sand rounded-lg px-3 py-1">{techItem.trim()}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-6 mt-auto">
                  <Link to="/contact" className="flex items-center text-maat-blue font-bold uppercase tracking-widest text-[11px] hover:text-maat-gold transition-colors">
                    {t.nav.contact} <ArrowRight size={16} className="ml-2" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Services;